import React, { useState, useEffect, useMemo } from 'react';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import StatusBadge from '../../components/common/StatusBadge';
import {
  Search,
  RefreshCw,
  Package,
  History
} from 'lucide-react';
import toast from 'react-hot-toast';

export const InventoryWorkspace = () => {
  const [products, setProducts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterHealth, setFilterHealth] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Adjustment Modal State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('Stock delivery receipt');
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const [prodsRes, logsRes] = await Promise.all([
        axios.get(`${baseUrl}api/products/data/all-products`),
        axios.get(`${baseUrl}api/admin/audit-logs?entity=Inventory&limit=20`)
      ]);

      setProducts(prodsRes.data?.data || []);
      setAuditLogs(logsRes.data?.data?.logs || []);
    } catch (err) {
      console.error('Failed to load inventory data:', err);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  // Health classification
  const classifiedProducts = useMemo(() => {
    return products.map(p => {
      const inv = Number(p.inventory || 0);
      let health = 'healthy';
      if (inv <= 0) health = 'out_of_stock';
      else if (inv < 10) health = 'low_stock';
      return { ...p, health, inv };
    });
  }, [products]);

  const counts = useMemo(() => {
    return {
      all: classifiedProducts.length,
      healthy: classifiedProducts.filter(p => p.health === 'healthy').length,
      low_stock: classifiedProducts.filter(p => p.health === 'low_stock').length,
      out_of_stock: classifiedProducts.filter(p => p.health === 'out_of_stock').length,
    };
  }, [classifiedProducts]);

  const filteredProducts = useMemo(() => {
    return classifiedProducts.filter(p => {
      if (filterHealth !== 'all' && p.health !== filterHealth) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const title = (p.title || '').toLowerCase();
        const sku = (p.articleCode || '').toLowerCase();
        const cat = (p.category || '').toLowerCase();
        return title.includes(q) || sku.includes(q) || cat.includes(q);
      }
      return true;
    });
  }, [classifiedProducts, filterHealth, searchQuery]);

  const handleOpenAdjustModal = (product) => {
    setSelectedProduct(product);
    setAdjustmentQty('');
    setAdjustmentReason('Stock delivery receipt');
    setCustomReason('');
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;
    const qty = Number(adjustmentQty);
    if (isNaN(qty) || qty === 0) {
      toast.error('Enter a valid non-zero adjustment quantity');
      return;
    }

    const finalReason = adjustmentReason === 'Other' ? customReason.trim() : adjustmentReason;
    if (!finalReason) {
      toast.error('Operational reason is mandatory');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${baseUrl}api/admin/inventory/adjust`, {
        productId: selectedProduct._id,
        adjustment: qty,
        reason: finalReason
      });

      toast.success(`Inventory updated for ${selectedProduct.title}`);
      setSelectedProduct(null);
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to adjust stock');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Inventory & Stock Control</h1>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-700">
              {products.length} Total SKUs
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Audit stock levels, reorder thresholds, and record reason-justified inventory movements.
          </p>
        </div>

        <button
          onClick={fetchInventory}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg text-xs font-medium transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stock Health KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => setFilterHealth(filterHealth === 'healthy' ? 'all' : 'healthy')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filterHealth === 'healthy'
              ? 'bg-emerald-50/80 border-emerald-500 shadow-xs'
              : 'bg-white border-stone-200 hover:border-stone-300'
          }`}
        >
          <div className="text-xs font-bold uppercase text-emerald-800 tracking-wider mb-1">
            Healthy Stock (&ge;10)
          </div>
          <div className="text-2xl font-bold text-emerald-950 font-mono">{counts.healthy}</div>
          <p className="text-[11px] text-stone-500 mt-1">Catalog units operating safely above reorder threshold.</p>
        </button>

        <button
          onClick={() => setFilterHealth(filterHealth === 'low_stock' ? 'all' : 'low_stock')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filterHealth === 'low_stock'
              ? 'bg-amber-50/80 border-amber-500 shadow-xs'
              : 'bg-white border-stone-200 hover:border-stone-300'
          }`}
        >
          <div className="text-xs font-bold uppercase text-amber-800 tracking-wider mb-1">
            Low Stock Warning (1–9)
          </div>
          <div className="text-2xl font-bold text-amber-950 font-mono">{counts.low_stock}</div>
          <p className="text-[11px] text-stone-500 mt-1">Products nearing depletion; replenish before stockout.</p>
        </button>

        <button
          onClick={() => setFilterHealth(filterHealth === 'out_of_stock' ? 'all' : 'out_of_stock')}
          className={`p-5 rounded-xl border text-left transition-all ${
            filterHealth === 'out_of_stock'
              ? 'bg-rose-50/80 border-rose-500 shadow-xs'
              : 'bg-white border-stone-200 hover:border-stone-300'
          }`}
        >
          <div className="text-xs font-bold uppercase text-rose-800 tracking-wider mb-1">
            Out of Stock (0 units)
          </div>
          <div className="text-2xl font-bold text-rose-950 font-mono">{counts.out_of_stock}</div>
          <p className="text-[11px] text-stone-500 mt-1">Active items with zero units; requires restock or draft status.</p>
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-xl border border-stone-200 shadow-xs">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search product title, SKU / articleCode, category..."
            className="w-full pl-9 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 placeholder-stone-400 focus:outline-none"
          />
        </div>

        {filterHealth !== 'all' && (
          <button
            onClick={() => setFilterHealth('all')}
            className="text-xs text-stone-500 hover:text-stone-900 underline whitespace-nowrap"
          >
            Clear Health Filter
          </button>
        )}
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200 text-stone-500 font-semibold uppercase tracking-wider text-[10px]">
                <th className="p-3.5">Product</th>
                <th className="p-3.5">SKU / Code</th>
                <th className="p-3.5">Category</th>
                <th className="p-3.5">Price</th>
                <th className="p-3.5">Current Stock</th>
                <th className="p-3.5">Health</th>
                <th className="p-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filteredProducts.map(prod => (
                <tr key={prod._id} className="hover:bg-amber-50/30 transition-colors">
                  <td className="p-3.5 flex items-center gap-3">
                    {prod.image?.[0]?.src ? (
                      <img src={prod.image[0].src} alt="" className="w-9 h-9 object-cover rounded-lg border border-stone-200" />
                    ) : (
                      <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center text-stone-400">
                        <Package className="w-4 h-4" />
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-stone-900">{prod.title}</div>
                      <div className="text-[10px] text-stone-400 capitalize">{prod.broadCategory}</div>
                    </div>
                  </td>

                  <td className="p-3.5 font-mono font-bold text-stone-700">
                    {prod.articleCode}
                  </td>

                  <td className="p-3.5 text-stone-600 capitalize">
                    {prod.category}
                  </td>

                  <td className="p-3.5 font-semibold text-stone-900">
                    {prod.numericPrice ? `£${prod.numericPrice}` : prod.price}
                  </td>

                  <td className="p-3.5 font-mono font-bold text-base text-stone-900">
                    {prod.inv}
                  </td>

                  <td className="p-3.5">
                    <StatusBadge type="inventory" status={prod.health} />
                  </td>

                  <td className="p-3.5 text-right">
                    <button
                      onClick={() => handleOpenAdjustModal(prod)}
                      className="px-3 py-1.5 bg-stone-900 hover:bg-stone-800 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                    >
                      Adjust Stock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Inventory Audit History */}
      {auditLogs.length > 0 && (
        <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm space-y-3">
          <div className="flex items-center gap-2 text-stone-900 font-semibold text-sm">
            <History className="w-4 h-4 text-stone-500" />
            <span>Recent Reason-Logged Adjustments</span>
          </div>

          <div className="divide-y divide-stone-100 text-xs">
            {auditLogs.map(log => (
              <div key={log._id} className="py-2.5 flex items-center justify-between gap-4">
                <div>
                  <span className="font-semibold text-stone-900">{log.details?.title || 'SKU Item'} </span>
                  <span className="text-stone-500 font-mono">({log.details?.articleCode})</span>
                  <div className="text-[11px] text-stone-400">
                    Reason: <span className="text-stone-700 italic">&ldquo;{log.details?.reason || 'Adjustment'}&rdquo;</span> • By {log.actorEmail}
                  </div>
                </div>

                <div className="text-right">
                  <span className={`font-mono font-bold text-xs ${Number(log.details?.adjustment) > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {Number(log.details?.adjustment) > 0 ? `+${log.details?.adjustment}` : log.details?.adjustment} units
                  </span>
                  <div className="text-[10px] text-stone-400">{new Date(log.createdAt).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Stock Adjustment Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6 max-w-md w-full">
            <h3 className="font-bold text-stone-900 text-sm mb-1">
              Adjust Stock: {selectedProduct.title}
            </h3>
            <p className="text-xs text-stone-500 mb-4">
              Current inventory: <span className="font-bold text-stone-900">{selectedProduct.inventory} units</span>
            </p>

            <form onSubmit={handleAdjustSubmit} className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-stone-700 mb-1">
                  Adjustment (+ for received stock, - for write-offs)
                </label>
                <input
                  type="number"
                  value={adjustmentQty}
                  onChange={(e) => setAdjustmentQty(e.target.value)}
                  placeholder="e.g. +50 or -5"
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs font-mono font-bold text-stone-900 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-stone-700 mb-1">
                  Operational Reason (Required for Audit Log)
                </label>
                <select
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-800 focus:outline-none"
                >
                  <option value="Stock delivery receipt">Stock delivery receipt (+)</option>
                  <option value="Physical cycle count correction">Physical cycle count correction</option>
                  <option value="Damaged unit write-off">Damaged unit write-off (-)</option>
                  <option value="Customer return restocked">Customer return restocked (+)</option>
                  <option value="Showroom / photo sample allocation">Showroom / photo sample allocation (-)</option>
                  <option value="Other">Other (custom justification)</option>
                </select>
              </div>

              {adjustmentReason === 'Other' && (
                <div>
                  <label className="block text-[11px] font-semibold text-stone-700 mb-1">
                    Specific Justification
                  </label>
                  <textarea
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Provide detailed reason for inventory change..."
                    rows={2}
                    className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-xs text-stone-900 focus:outline-none"
                    required
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                <button
                  type="button"
                  onClick={() => setSelectedProduct(null)}
                  className="px-3 py-1.5 bg-stone-100 text-stone-700 rounded-lg text-xs font-semibold hover:bg-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-1.5 bg-stone-900 text-white rounded-lg text-xs font-semibold hover:bg-stone-800 disabled:opacity-50"
                >
                  {isSubmitting ? 'Recording...' : 'Commit Adjustment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryWorkspace;
